import { useApiContext } from '@/contexts/apiContext';
import { useElementSize } from '@custom-react-hooks/use-element-size';
import { Box, Step, StepLabel, Stepper, useTheme } from '@mui/material';
import { useConfirm } from 'material-ui-confirm';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import ErrorMessage from '../../errorMessage';
import ProgressIndicator from '../../progressIndicator';
import StepComplete from './stepComplete';
import StepCustomPrompt from './stepCustomPrompt';
import StepDataSource from './stepDatasource';
import StepDescribeReq from './stepDescribeReq';
import StepExternalApi from './stepExternalApi';
import StepJsonFile from './stepJsonFile';
import StepLlm from './stepLlm';
import StepProcessing from './stepProcessing';
import StepReviewReq from './stepReviewReq';

type Props = { maxHeight: number };

const UserRequirements = ({}: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();
  const theme = useTheme();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const [containerRef, containerSize] = useElementSize();
  const [topPanelRef, topPanelSize] = useElementSize();

  const surveyId = 'requirementDescription';

  const [questions, setQuestions] = useState<Record<string, any>>({});
  const [step, setStep] = useState('describeRequirements');
  const [activeStep, setActiveStep] = useState(0);
  const [userResponses, setUserResponses] = useState<Record<string, any>>({
    documentProps: ['imageInDocuments'],
    targetLanguage: 'en',
    dataSourceType: 'userUpload',
    multiModalOption: 'standardPrompts',
    externalApiSettings: {
      authenticationType: 'None',
    },
    llmOption: 'builtIn',
    llmSettings: {},
  });
  const [surveyResponseId, setSurveyResponseId] = useState<string>('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    const getSurvey = async () => {
      try {
        setLoading(true);
        const response = await apiContext.getUserSurvey(surveyId as string);
        setQuestions(response.surveyQuestions || {});
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    if (surveyId) {
      getSurvey();
    }
  }, [surveyId]);

  const onUserRquirementsChange = (userRequirements: string) => {
    setUserResponses({ ...userResponses, userRequirements });
  };

  const setParsedRequirements = (parsedRequirements: any) => {
    setUserResponses({ ...userResponses, parsedRequirements });
  };

  const onParsedRequirementsChange = (questionId: string, response: any) => {
    setUserResponses({
      ...userResponses,
      parsedRequirements: { ...userResponses.parsedRequirements, [questionId]: response },
    });
  };

  const onBack = () => {
    if (step === 'reviewRequirements') {
      setStep('describeRequirements');
    } else if (step === 'dataSource') {
      setStep('reviewRequirements');
    } else if (step === 'llmOption') {
      if (userResponses.parsedRequirements.dataSourceType !== 'userUpload') {
        setStep('dataSource');
      } else {
        setStep('reviewRequirements');
      }
    } else if (step === 'jsonSchema' || step === 'jsonSample') {
      if (userResponses.parsedRequirements.llmOption !== 'builtIn') {
        setStep('llmOption');
      } else if (userResponses.parsedRequirements.dataSourceType !== 'userUpload') {
        setStep('dataSource');
      } else {
        setStep('reviewRequirements');
      }
    } else if (step === 'externalApi') {
      if (userResponses.parsedRequirements.extractionType === 'jsonSchema') {
        setStep('jsonSchema');
      } else if (userResponses.parsedRequirements.extractionType === 'jsonSample') {
        setStep('jsonSample');
      } else if (userResponses.parsedRequirements.llmOption !== 'builtIn') {
        setStep('llmOption');
      } else if (userResponses.parsedRequirements.dataSourceType !== 'userUpload') {
        setStep('dataSource');
      } else {
        setStep('reviewRequirements');
      }
    } else if (step === 'customPrompt') {
      if (userResponses.parsedRequirements.postProcessingActions?.includes('externalApi')) {
        setStep('externalApi');
      } else if (userResponses.parsedRequirements.extractionType === 'jsonSchema') {
        setStep('jsonSchema');
      } else if (userResponses.parsedRequirements.extractionType === 'jsonSample') {
        setStep('jsonSample');
      } else if (userResponses.parsedRequirements.llmOption !== 'builtIn') {
        setStep('llmOption');
      } else if (userResponses.parsedRequirements.dataSourceType !== 'userUpload') {
        setStep('dataSource');
      } else {
        setStep('reviewRequirements');
      }
    } else if (step === 'processing') {
      if (userResponses.parsedRequirements.postProcessingActions?.includes('customPrompt')) {
        setStep('customPrompt');
      } else if (userResponses.parsedRequirements.postProcessingActions?.includes('externalApi')) {
        setStep('externalApi');
      } else if (userResponses.parsedRequirements.extractionType === 'jsonSchema') {
        setStep('jsonSchema');
      } else if (userResponses.parsedRequirements.extractionType === 'jsonSample') {
        setStep('jsonSample');
      } else if (userResponses.parsedRequirements.llmOption !== 'builtIn') {
        setStep('llmOption');
      } else if (userResponses.parsedRequirements.dataSourceType !== 'userUpload') {
        setStep('dataSource');
      } else {
        setStep('reviewRequirements');
      }
    }
  };

  const onNext = async () => {
    if (step === 'describeRequirements') {
      setStep('reviewRequirements');
      setActiveStep(1);
    } else if (step === 'reviewRequirements') {
      if (userResponses.parsedRequirements.dataSourceType !== 'userUpload') {
        setStep('dataSource');
        setActiveStep(1);
      } else if (userResponses.parsedRequirements.llmOption !== 'builtIn') {
        setStep('llmOption');
        setActiveStep(1);
      } else if (userResponses.parsedRequirements.extractionType === 'jsonSchema') {
        setStep('jsonSchema');
        setActiveStep(1);
      } else if (userResponses.parsedRequirements.extractionType === 'jsonSample') {
        setStep('jsonSample');
        setActiveStep(1);
      } else if (userResponses.parsedRequirements.postProcessingActions?.includes('externalApi')) {
        setStep('externalApi');
        setActiveStep(1);
      } else if (userResponses.parsedRequirements.postProcessingActions?.includes('customPrompt')) {
        setStep('customPrompt');
        setActiveStep(1);
      } else {
        setStep('processing');
        setActiveStep(2);
      }
    } else if (step === 'dataSource') {
      if (userResponses.parsedRequirements.llmOption !== 'builtIn') {
        setStep('llmOption');
        setActiveStep(1);
      } else if (userResponses.parsedRequirements.extractionType === 'jsonSchema') {
        setStep('jsonSchema');
        setActiveStep(1);
      } else if (userResponses.parsedRequirements.extractionType === 'jsonSample') {
        setStep('jsonSample');
        setActiveStep(1);
      } else if (userResponses.parsedRequirements.postProcessingActions?.includes('externalApi')) {
        setStep('externalApi');
        setActiveStep(1);
      } else if (userResponses.parsedRequirements.postProcessingActions?.includes('customPrompt')) {
        setStep('customPrompt');
        setActiveStep(1);
      } else {
        setStep('processing');
        setActiveStep(2);
      }
    } else if (step === 'llmOption') {
      if (userResponses.parsedRequirements.extractionType === 'jsonSchema') {
        setStep('jsonSchema');
        setActiveStep(1);
      } else if (userResponses.parsedRequirements.extractionType === 'jsonSample') {
        setStep('jsonSample');
        setActiveStep(1);
      } else if (userResponses.parsedRequirements.postProcessingActions?.includes('externalApi')) {
        setStep('externalApi');
        setActiveStep(1);
      } else if (userResponses.parsedRequirements.postProcessingActions?.includes('customPrompt')) {
        setStep('customPrompt');
        setActiveStep(1);
      } else {
        setStep('processing');
        setActiveStep(2);
      }
    } else if (step === 'jsonSchema' || step === 'jsonSample') {
      if (userResponses.parsedRequirements.postProcessingActions?.includes('externalApi')) {
        setStep('externalApi');
        setActiveStep(1);
      } else if (userResponses.parsedRequirements.postProcessingActions?.includes('customPrompt')) {
        setStep('customPrompt');
        setActiveStep(1);
      } else {
        setStep('processing');
        setActiveStep(2);
      }
    } else if (step === 'externalApi') {
      if (userResponses.parsedRequirements.postProcessingActions?.includes('customPrompt')) {
        setStep('customPrompt');
        setActiveStep(1);
      } else {
        setStep('processing');
        setActiveStep(2);
      }
    } else if (step === 'customPrompt') {
      setStep('processing');
      setActiveStep(2);
    } else if (step === 'processing') {
      setStep('complete');
      setActiveStep(2);
    }
  };

  const onCancel = () => {
    confirm({ description: 'Are you sure you want to cancel?', title: '' }).then(async () => {
      navigate('/buildOptions');
    });
  };

  if (loading) {
    return <ProgressIndicator />;
  }

  return (
    <Box
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}
      ref={containerRef}
    >
      {/* Progress Stepper */}
      <Box
        ref={topPanelRef}
        sx={{
          px: 3,
          py: 0.75,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'rgba(255,255,255,0.8)',
        }}
      >
        <Stepper
          activeStep={activeStep}
          sx={{
            maxWidth: 400,
            mx: 'auto',
            '& .MuiStepIcon-root.Mui-active': { color: theme.palette.primary.main },
            '& .MuiStepIcon-root.Mui-completed': { color: theme.palette.secondary.main },
            '& .MuiStepIcon-root': { fontSize: 16 },
            '& .MuiStepLabel-label': { fontSize: { xs: '9px', sm: '11px' }, fontWeight: 600 },
            '& .MuiStepConnector-line': { minWidth: { xs: 8, sm: 16 } },
          }}
        >
          <Step>
            <StepLabel>{t('Describe')}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{t('Review & Configure')}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{t('Process & Create')}</StepLabel>
          </Step>
        </Stepper>
      </Box>

      {error && (
        <Box sx={{ px: 4, pt: 2 }}>
          <ErrorMessage error={error} />
        </Box>
      )}

      {/* Step Content */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', justifyContent: 'center', p: 1 }}>
        <Box sx={{ width: '100%', maxWidth: { xs: '100%', md: '66.67vw' }, height: '100%' }}>
          {step === 'describeRequirements' && (
            <StepDescribeReq
              userResponses={userResponses}
              maxHeight={containerSize.height - topPanelSize.height - 10}
              onUserRquirementsChange={onUserRquirementsChange}
              onBack={onBack}
              onCancel={onCancel}
              onNext={onNext}
            />
          )}
          {step === 'reviewRequirements' && (
            <StepReviewReq
              surveyId={surveyId}
              userResponses={userResponses}
              maxHeight={containerSize.height - topPanelSize.height - 10}
              setParsedRequirements={setParsedRequirements}
              onBack={onBack}
              onCancel={onCancel}
              onNext={onNext}
            />
          )}
          {step === 'dataSource' && (
            <StepDataSource
              questions={questions}
              userResponses={userResponses}
              maxHeight={containerSize.height - topPanelSize.height - 10}
              onParsedRequirementsChange={onParsedRequirementsChange}
              onBack={onBack}
              onCancel={onCancel}
              onNext={onNext}
            />
          )}
          {step === 'llmOption' && (
            <StepLlm
              questions={questions}
              userResponses={userResponses}
              maxHeight={containerSize.height - topPanelSize.height - 10}
              onParsedRequirementsChange={onParsedRequirementsChange}
              onBack={onBack}
              onCancel={onCancel}
              onNext={onNext}
            />
          )}
          {(step === 'jsonSchema' || step === 'jsonSample') && (
            <StepJsonFile
              questions={questions}
              userResponses={userResponses}
              maxHeight={containerSize.height - topPanelSize.height - 10}
              onParsedRequirementsChange={onParsedRequirementsChange}
              onBack={onBack}
              onCancel={onCancel}
              onNext={onNext}
            />
          )}
          {step === 'externalApi' && (
            <StepExternalApi
              questions={questions}
              userResponses={userResponses}
              maxHeight={containerSize.height - topPanelSize.height - 10}
              onParsedRequirementsChange={onParsedRequirementsChange}
              onBack={onBack}
              onCancel={onCancel}
              onNext={onNext}
            />
          )}
          {step === 'customPrompt' && (
            <StepCustomPrompt
              questions={questions}
              userResponses={userResponses}
              maxHeight={containerSize.height - topPanelSize.height - 10}
              onParsedRequirementsChange={onParsedRequirementsChange}
              onBack={onBack}
              onCancel={onCancel}
              onNext={onNext}
            />
          )}
          {step === 'processing' && (
            <StepProcessing
              surveyId={surveyId}
              userResponses={userResponses}
              onSurveyResponseId={setSurveyResponseId}
              maxHeight={containerSize.height - topPanelSize.height - 10}
              onBack={onBack}
              onCancel={onCancel}
              onNext={onNext}
            />
          )}
          {step === 'complete' && (
            <StepComplete
              surveyId={surveyId}
              responseId={surveyResponseId}
              userResponses={userResponses}
              maxHeight={containerSize.height - topPanelSize.height - 10}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default UserRequirements;
